import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, AlertTriangle, Truck, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { money, StatusBadge } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["Awaiting Fulfillment", "Supplier Order Created", "Processing", "Shipped", "In Transit", "Delivered", "Cancelled", "Refunded", "Requires Review"];

export default function OrderDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [verify, setVerify] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => api.get(`/admin/orders/${id}`).then((r) => setData(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  const runVerify = async () => {
    setBusy("verify");
    try { const { data } = await api.post(`/admin/orders/${id}/verify`); setVerify(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const approve = async (force = false) => {
    setBusy("approve");
    try {
      const { data } = await api.post(`/admin/orders/${id}/approve-fulfillment${force ? "?force=true" : ""}`);
      setVerify(data.verification);
      if (data.blocked) {
        toast.error("Blocked by profit protection — review warnings below");
      } else {
        toast.success(force ? "Approved (override) — supplier order(s) created" : "Supplier order(s) created");
      }
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const syncTracking = async () => {
    setBusy("track");
    try { await api.post(`/admin/orders/${id}/sync-tracking`); toast.success("Tracking synced"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const setStatus = async (status) => {
    try { await api.post(`/admin/orders/${id}/status`, { status }); toast.success(`Status → ${status}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const refund = async () => {
    if (!window.confirm("Refund this order?")) return;
    try { await api.post(`/admin/orders/${id}/refund`); toast.success("Refunded"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <div className="animate-pulse h-96 bg-zinc-100 rounded-xl" />;
  const o = data.order;
  const paid = o.payment_status === "paid";
  const canApprove = paid && ["Awaiting Fulfillment", "Requires Review"].includes(o.status);

  return (
    <div className="space-y-5 max-w-5xl">
      <Link to="/admin/orders" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900"><ArrowLeft size={15} />Back to orders</Link>

      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-bold tracking-tight font-mono">{o.order_number}</h1>
            <StatusBadge status={o.status} />
          </div>
          <p className="text-sm text-zinc-500 mt-1">{new Date(o.created_at).toLocaleString()}</p>
        </div>
        <div className="flex gap-2">
          <Select onValueChange={setStatus}>
            <SelectTrigger className="w-44" data-testid="set-status"><SelectValue placeholder="Set status" /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          {paid && !["Refunded"].includes(o.status) && <Button variant="outline" onClick={refund} data-testid="refund-btn">Refund</Button>}
        </div>
      </div>

      {o.flagged && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3" data-testid="profit-warning">
          <AlertTriangle size={18} className="text-red-600 shrink-0 mt-0.5" />
          <div><p className="font-semibold text-red-700 text-sm">Requires Review</p><p className="text-xs text-red-600 mt-0.5">{o.flag_reason}</p></div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Items */}
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <h3 className="font-semibold text-sm mb-4">Items</h3>
            <div className="space-y-3">
              {o.items.map((i, idx) => (
                <div key={idx} className="flex gap-3 items-center">
                  <img src={i.image} alt="" className="w-12 h-14 object-cover rounded-md" />
                  <div className="flex-1"><p className="text-sm font-medium">{i.title}</p>
                    <p className="text-xs text-zinc-400">{i.variant_label || "—"} · Qty {i.quantity} · {i.supplier_name}</p>
                    <p className="text-xs text-zinc-400 font-mono">Supplier SKU: {i.supplier_sku}</p></div>
                  <div className="text-right text-sm">
                    <p className="font-medium">{money(i.selling_price * i.quantity)}</p>
                    <p className="text-xs text-zinc-400">cost {money(i.supplier_price * i.quantity)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Fulfillment */}
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Supplier Fulfillment</h3>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={runVerify} disabled={busy === "verify"} data-testid="verify-btn">
                  {busy === "verify" ? <Loader2 size={14} className="animate-spin" /> : "Verify Stock & Profit"}
                </Button>
                {canApprove && (
                  <Button size="sm" onClick={() => approve(false)} disabled={busy === "approve"} data-testid="approve-fulfillment-btn">
                    {busy === "approve" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}Approve Fulfillment
                  </Button>
                )}
                {canApprove && verify && !verify.ok && (
                  <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => approve(true)} disabled={busy === "approve"} data-testid="approve-anyway-btn">
                    <AlertTriangle size={14} className="mr-1.5" />Approve Anyway
                  </Button>
                )}
              </div>
            </div>

            {verify && (
              <div className={`rounded-lg p-3 mb-4 text-xs ${verify.ok ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`} data-testid="verify-result">
                <p className="font-semibold mb-1">{verify.ok ? "All checks passed ✓" : "Warnings found:"}</p>
                {verify.warnings.map((w, i) => <p key={i}>• {w}</p>)}
                {verify.checks.map((c, i) => <p key={`c${i}`} className="text-zinc-500">{c.item}: stock {c.available}, supplier {money(c.current_supplier_price)} (was {money(c.original_supplier_price)}), profit {money(c.profit)}</p>)}
              </div>
            )}

            {data.supplier_orders.length === 0 ? (
              <p className="text-sm text-zinc-400">No supplier order created yet. Review and approve to send to supplier.</p>
            ) : (
              <div className="space-y-3">
                {data.supplier_orders.map((so) => (
                  <div key={so.id} className="border border-zinc-100 rounded-lg p-3" data-testid={`supplier-order-${so.id}`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium capitalize">{so.supplier} {so.simulated && <span className="text-[10px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded">SIMULATED</span>}</p>
                        <p className="text-xs font-mono text-zinc-500">{so.supplier_order_id || "—"}</p>
                      </div>
                      <StatusBadge status={so.status} />
                    </div>
                    {so.tracking_number && <p className="text-xs text-zinc-500 mt-2 font-mono">{so.courier}: {so.tracking_number}</p>}
                    <p className="text-xs text-zinc-400 mt-1">Supplier cost: {money(so.supplier_cost)}</p>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={syncTracking} disabled={busy === "track"} data-testid="sync-tracking-btn">
                  {busy === "track" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Truck size={14} className="mr-1.5" />}Sync Tracking
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <div className="bg-white border border-zinc-200 rounded-xl p-5 text-sm">
            <h3 className="font-semibold mb-3">Summary</h3>
            <div className="space-y-2 text-xs">
              <Row label="Subtotal" value={money(o.subtotal)} />
              {o.discount_amount > 0 && <Row label={`Discount (${o.discount_code})`} value={`-${money(o.discount_amount)}`} />}
              <Row label="Shipping" value={money(o.shipping_fee)} />
              <div className="border-t border-zinc-100 pt-2"><Row label="Total" value={money(o.total)} bold /></div>
              <Row label="Supplier cost" value={money(o.supplier_cost)} muted />
              <Row label="Supplier shipping" value={money(o.supplier_shipping)} muted />
              <Row label="Payment fee" value={money(o.payment_fee)} muted />
              <div className="border-t border-zinc-100 pt-2"><Row label="Est. profit" value={money(o.estimated_profit)} green /></div>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-xl p-5 text-sm">
            <h3 className="font-semibold mb-3">Customer</h3>
            <p className="font-medium">{o.shipping_address.name}</p>
            <p className="text-zinc-500 text-xs">{o.email}</p>
            <p className="text-zinc-500 text-xs mt-2">{o.shipping_address.phone}</p>
            <p className="text-zinc-600 text-xs mt-2 leading-relaxed">{o.shipping_address.line1}, {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.postal_code}, {o.shipping_address.country}</p>
            <p className="text-xs text-zinc-400 mt-2">Method: {o.shipping_method}{o.guest ? " · Guest" : ""}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, bold, muted, green }) => (
  <div className="flex justify-between"><span className={muted ? "text-zinc-400" : "text-zinc-500"}>{label}</span>
    <span className={`${bold ? "font-bold text-sm" : ""} ${green ? "text-green-600 font-semibold" : ""}`}>{value}</span></div>
);
